import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { ComparedMetric, LearningImpactReport, LearningTarget, MetricComparison, insightsApi } from '../../services/insightsApi';
import { fmtMs, fmtNum, fmtPct } from '../performance/common';
import { TargetLink, fmtCredits, links, useReport } from './common';

const WINDOWS = [7, 30, 90];

const METRICS: { key: ComparedMetric; label: string; help: string }[] = [
  { key: 'success_rate', label: 'Success', help: 'Share of runs that succeeded. Higher is better.' },
  { key: 'backup_share', label: 'Answered by backup', help: "Share of runs a backup model answered because the first one failed or was too slow. Lower is better." },
  { key: 'invalid_output_share', label: 'Invalid output', help: 'Share of runs where a model answer failed to parse at least once. Functions only. Lower is better.' },
  { key: 'p95_ms', label: 'p95 time', help: 'Only 5% of successful runs took longer. Lower is better.' },
  { key: 'credits_per_1k_runs', label: 'Credits / 1k runs', help: 'AI credits per 1,000 runs. Lower is better.' },
];

const fmtMetric = (key: ComparedMetric, v: number | null) => {
  if (key === 'p95_ms') return fmtMs(v);
  if (key === 'credits_per_1k_runs') return fmtCredits(v);
  return fmtPct(v);
};

const fmtDelta = (m: MetricComparison) => {
  if (m.delta === null) return '–';
  if (m.metric === 'p95_ms' || m.metric === 'credits_per_1k_runs') {
    if (m.relative === null) return m.delta === 0 ? '0%' : '–';
    const pct = Math.round(m.relative * 1000) / 10;
    return `${pct > 0 ? '+' : ''}${pct}%`;
  }
  const pts = Math.round(m.delta * 1000) / 10;
  return `${pts > 0 ? '+' : ''}${pts} pts`;
};

const DIRECTION_COLOR = { better: 'success.main', worse: 'error.main', same: 'text.secondary' } as const;

const MetricCell: React.FC<{ m?: MetricComparison; enough: boolean }> = ({ m, enough }) => {
  if (!m || (m.baseline === null && m.treated === null)) {
    return (
      <Typography variant="body2" color="text.disabled">
        –
      </Typography>
    );
  }
  const color = enough && m.direction ? DIRECTION_COLOR[m.direction] : 'text.disabled';
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: enough ? undefined : 'text.disabled' }}>
        {fmtMetric(m.metric, m.treated)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        was {fmtMetric(m.metric, m.baseline)}
      </Typography>
      <Typography variant="caption" sx={{ color, fontWeight: enough && m.direction !== 'same' ? 600 : 400 }}>
        {fmtDelta(m)}
        {enough && m.direction === 'same' ? ' (no real change)' : ''}
      </Typography>
    </Box>
  );
};

const methodText = (t: LearningTarget) => {
  if (t.method === 'with_without') return { short: 'With vs without', long: 'Runs that received learned knowledge compared with runs that did not, in the same window.' };
  if (t.method === 'before_after') {
    const cut = t.window.cut ? new Date(t.window.cut).toLocaleDateString() : 'activation';
    return {
      short: `Before vs after ${cut}`,
      long: `No run-level data to compare, so this compares equal windows before and after learned knowledge was first put in use (${cut}).`,
    };
  }
  return { short: 'No runs', long: 'No runs in this window.' };
};

const sideLabels = (t: LearningTarget) => (t.method === 'before_after' ? ['before', 'after'] : ['without', 'with']);

export const LearningImpact: React.FC<{ customerId?: string | null }> = ({ customerId }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => insightsApi(getIdToken), [getIdToken]);
  const [days, setDays] = useState(30);
  const load = useCallback(() => api.learningImpact(days, customerId), [api, days, customerId]);
  const { data, loading, error } = useReport<LearningImpactReport>(load);

  return (
    <Box sx={{ opacity: loading && data ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: 820 }}>
          <Typography variant="body2" color="text.secondary">
            For each function and chain with self-improvement, how runs that used learned knowledge compare with runs that didn't. If there are
            no such runs yet, equal windows before and after learned knowledge was first put in use are compared instead. Function numbers
            cover direct calls only (learned knowledge isn't used inside chains for a function).
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            This is an observation, not a controlled test: runs only get learned knowledge when their input matches something learned, so the
            two groups can differ in other ways. Comparisons need at least {data?.min_runs ?? 30} runs on each side; smaller ones are greyed
            out.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          {WINDOWS.map(d => (
            <ToggleButton key={d} value={d}>
              {d}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && data.targets.length === 0 && (
        <Alert severity="info">
          No functions or chains have self-improvement turned on. Turn it on from a function's or chain's Self-improvement page to start
          learning from its runs.
        </Alert>
      )}

      {data && data.targets.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Function or chain</TableCell>
                  <TableCell>Compared</TableCell>
                  {METRICS.map(m => (
                    <TableCell key={m.key}>
                      <Tooltip title={m.help}>
                        <span style={{ cursor: 'help' }}>{m.label}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.targets.map(t => {
                  const enough = t.comparison.enough;
                  const byMetric = Object.fromEntries(t.comparison.metrics.map(m => [m.metric, m])) as Record<ComparedMetric, MetricComparison>;
                  const method = methodText(t);
                  const [baseLabel, treatedLabel] = sideLabels(t);
                  return (
                    <TableRow key={`${t.type}:${t.id}`} sx={{ verticalAlign: 'top', bgcolor: enough ? undefined : 'action.hover' }}>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip size="small" variant="outlined" label={t.type} sx={{ height: 18, fontSize: 10.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              <Link component={RouterLink} to={links.learning(t.type, t.id)} underline="hover">
                                {t.name}
                              </Link>
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {fmtNum(t.entries_active)} entries in use
                            {t.entries_waiting ? ` · ${fmtNum(t.entries_waiting)} waiting` : ''}
                            {!t.enabled ? ' · learning paused' : ''}
                          </Typography>
                          <Typography variant="caption">
                            <TargetLink type={t.type} id={t.performance_id} name="Performance" customerId={customerId} />
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Tooltip title={method.long}>
                          <Typography variant="body2" sx={{ cursor: 'help' }}>
                            {method.short}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {fmtNum(t.treated.runs)} {treatedLabel} · {fmtNum(t.baseline.runs)} {baseLabel}
                        </Typography>
                        {!enough && t.method !== 'none' && (
                          <Chip size="small" variant="outlined" label={`Need ${data.min_runs}+ runs per side`} sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
                        )}
                      </TableCell>
                      {METRICS.map(m => (
                        <TableCell key={m.key}>
                          <MetricCell m={byMetric[m.key]} enough={enough} />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Each cell shows the value with learned knowledge (or after), what it was without (or before), and the change: green is better, red
            is worse, grey is within noise (1 point for shares, 5% for time and credits). Chain credits come from each chain run's own total.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default LearningImpact;
